/**
 * Post-processing algorithms for SDF volumes.
 *
 * This module provides:
 * - Small part removal using connected component labeling
 * - Cavity filling using connected component labeling
 */

import { maybeYield } from './utils';

/**
 * Connected component labeling using union-find, working directly on SDF data.
 * Returns an array of labels (0 = background, 1+ = component ID).
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param labelInside If true, labels voxels >= threshold; if false, labels voxels < threshold
 * @param signal Optional AbortSignal for cancellation
 */
async function labelConnectedComponents3D(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    labelInside: boolean,
    signal?: AbortSignal
): Promise<{ labels: Int32Array; numComponents: number }> {
    const N = nx * ny * nz;
    const plane = nx * ny;
    const labels = new Int32Array(N);
    const parent = new Int32Array(N);
    // Rank array not needed - use weighted union by size instead, stored in parent as negative values
    // parent[i] >= 0 means parent[i] is the parent index
    // parent[i] < 0 means i is a root with size = -parent[i]

    // Precalculate neighbor offsets
    const offsetX = 1;
    const offsetY = nx;
    const offsetZ = plane;

    // SDF skip thresholds - if far from surface, skip ahead
    const skipLowThreshold = 16; // Definitely outside
    const skipHighThreshold = 240; // Definitely inside
    const skipAmount = 3;

    // Initialize all as roots with size 1 (stored as -1)
    parent.fill(-1);

    // Find with path compression (iterative to avoid stack overflow)
    function find(x: number): number {
        let root = x;
        while (parent[root] >= 0) {
            root = parent[root];
        }
        // Path compression
        while (parent[x] >= 0) {
            const next = parent[x];
            parent[x] = root;
            x = next;
        }
        return root;
    }

    // Union by size (size stored as negative value in root's parent)
    function union(x: number, y: number): void {
        const rootX = find(x);
        const rootY = find(y);
        if (rootX !== rootY) {
            // parent[root] is negative size, so smaller (more negative) = larger set
            if (parent[rootX] <= parent[rootY]) {
                // rootX has larger or equal size
                parent[rootX] += parent[rootY]; // Merge sizes
                parent[rootY] = rootX;
            } else {
                parent[rootY] += parent[rootX];
                parent[rootX] = rootY;
            }
        }
    }

    // Helper to check if voxel is foreground based on labelInside mode
    const isForeground = labelInside
        ? (val: number) => val >= threshold
        : (val: number) => val < threshold;

    // The "skip-worthy" condition: far from surface in the direction we DON'T want
    const canSkip = labelInside
        ? (val: number) => val < skipLowThreshold // Far outside, skip
        : (val: number) => val > skipHighThreshold; // Far inside, skip

    let lastYieldTick = performance.now();

    // First pass: assign provisional labels and record equivalences
    for (let z = 0; z < nz; z++) {
        const slice = sdfU8[z];
        const zOffset = z * plane;

        for (let y = 0; y < ny; y++) {
            const yOffset = y * nx;

            for (let x = 0; x < nx; x++) {
                const localIdx = yOffset + x;
                const i = zOffset + localIdx;
                const val = slice[localIdx];

                // SDF skip optimization: if far from surface in background direction, skip ahead
                if (canSkip(val)) {
                    const skip = Math.min(skipAmount, nx - 1 - x);
                    if (skip > 0) {
                        x += skip - 1; // -1 because loop will increment
                        continue;
                    }
                }

                if (!isForeground(val)) {
                    continue;
                } // Background

                // Check 6-connected neighbors (only those already visited)
                // Using precalculated offsets instead of idx3() calls
                const nX =
                    x > 0 && isForeground(slice[localIdx - 1])
                        ? i - offsetX
                        : -1;
                const nY =
                    y > 0 && isForeground(slice[localIdx - nx])
                        ? i - offsetY
                        : -1;
                const nZ =
                    z > 0 && isForeground(sdfU8[z - 1][localIdx])
                        ? i - offsetZ
                        : -1;

                if (nX < 0 && nY < 0 && nZ < 0) {
                    // New component - use own index as label
                    labels[i] = i;
                } else {
                    // Find minimum neighbor label
                    let minLabel = N; // Start with max possible
                    if (nX >= 0 && labels[nX] < minLabel) {
                        minLabel = labels[nX];
                    }
                    if (nY >= 0 && labels[nY] < minLabel) {
                        minLabel = labels[nY];
                    }
                    if (nZ >= 0 && labels[nZ] < minLabel) {
                        minLabel = labels[nZ];
                    }

                    labels[i] = minLabel;

                    // Union all neighbor labels with minLabel
                    if (nX >= 0) {
                        union(minLabel, labels[nX]);
                    }
                    if (nY >= 0) {
                        union(minLabel, labels[nY]);
                    }
                    if (nZ >= 0) {
                        union(minLabel, labels[nZ]);
                    }
                }
            }
        }

        // Yield every few slices to keep UI responsive
        if (z % 10 === 0) {
            lastYieldTick = await maybeYield(lastYieldTick, signal);
        }
    }

    // Second pass: flatten labels using union-find
    // Use typed array instead of Map for faster lookup (roots are indices 0..N-1)
    const rootToLabel = new Int32Array(N); // 0 = unassigned, >0 = label
    let nextLabel = 1;

    for (let z = 0; z < nz; z++) {
        const slice = sdfU8[z];
        const zOffset = z * plane;

        for (let localIdx = 0; localIdx < plane; localIdx++) {
            const i = zOffset + localIdx;
            if (isForeground(slice[localIdx])) {
                const root = find(labels[i]);
                let label = rootToLabel[root];
                if (label === 0) {
                    label = nextLabel++;
                    rootToLabel[root] = label;
                }
                labels[i] = label;
            } else {
                labels[i] = 0;
            }
        }

        // Yield every few slices
        if (z % 10 === 0) {
            lastYieldTick = await maybeYield(lastYieldTick, signal);
        }
    }

    return { labels, numComponents: nextLabel - 1 };
}

export interface RemoveSmallPartsResult {
    sdfU8: Uint8Array[];
    numComponentsFound: number;
    numComponentsRemoved: number;
}

/**
 * Remove small connected components from an SDF volume.
 * Components with volume below the threshold are removed.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param voxelVolumeMm3 Volume of a single voxel in mm³
 * @param minVolumeCm3 Minimum volume to keep in cm³
 * @param signal Optional AbortSignal for cancellation
 * @returns Result containing processed SDF and statistics
 */
export async function removeSmallParts(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    voxelVolumeMm3: number,
    minVolumeCm3: number,
    signal?: AbortSignal
): Promise<RemoveSmallPartsResult> {
    const N = nx * ny * nz;
    const plane = nx * ny;

    let lastYieldTick = performance.now();

    // Label connected components of the inside (foreground)
    const { labels, numComponents } = await labelConnectedComponents3D(
        sdfU8,
        nx,
        ny,
        nz,
        threshold,
        true, // labelInside = true
        signal
    );

    if (numComponents <= 1) {
        // 0 or 1 component - nothing to remove
        return {
            sdfU8: sdfU8.map((slice) => new Uint8Array(slice)),
            numComponentsFound: numComponents,
            numComponentsRemoved: 0,
        };
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Calculate volume of each component
    const componentVolumes = new Float64Array(numComponents + 1);
    for (let i = 0; i < N; i++) {
        if (labels[i] > 0) {
            componentVolumes[labels[i]]++;
        }
    }

    // Convert voxel counts to cm³
    const voxelVolumeCm3 = voxelVolumeMm3 / 1000; // mm³ to cm³
    for (let c = 1; c <= numComponents; c++) {
        componentVolumes[c] *= voxelVolumeCm3;
    }

    // Determine which components to keep and count removed
    const keepComponent = new Uint8Array(numComponents + 1);
    let numComponentsRemoved = 0;
    for (let c = 1; c <= numComponents; c++) {
        if (componentVolumes[c] >= minVolumeCm3) {
            keepComponent[c] = 1;
        } else {
            numComponentsRemoved++;
        }
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Create output - set removed voxels to just below threshold.
    // Using 127 (just below threshold 127.5) instead of 0 reduces surface shrinkage.
    // The FMM reinitialization will need to be called to compute proper metric distances.
    const REMOVED_VALUE = 127;
    const outU8: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        outU8[z] = new Uint8Array(plane);
        for (let i = 0; i < plane; i++) {
            const globalIdx = z * plane + i;
            const label = labels[globalIdx];
            if (label > 0 && keepComponent[label]) {
                outU8[z][i] = sdfU8[z][i];
            } else if (label > 0) {
                // This voxel belongs to a removed component
                outU8[z][i] = REMOVED_VALUE;
            } else {
                // Background - keep original value
                outU8[z][i] = sdfU8[z][i];
            }
        }

        // Yield every few slices
        if (z % 20 === 0) {
            lastYieldTick = await maybeYield(lastYieldTick, signal);
        }
    }

    return {
        sdfU8: outU8,
        numComponentsFound: numComponents,
        numComponentsRemoved,
    };
}

/**
 * Fill cavities (holes) in an SDF volume using connected component labeling.
 * Any enclosed cavity inside the structure will be filled.
 *
 * Uses union-find based labeling with amortized near-linear complexity
 * (O(n α(n)) with inverse Ackermann α) instead of the previous BFS flood fill
 * implementation, for much better performance on large volumes.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param signal Optional AbortSignal for cancellation
 * @returns Processed SDF as array of Uint8Array slices
 */
export async function fillCavities(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    signal?: AbortSignal
): Promise<Uint8Array[]> {
    const plane = nx * ny;

    let lastYieldTick = performance.now();

    // Label connected components of the background (outside regions)
    // labelInside=false means we label voxels where sdf < threshold
    const { labels, numComponents } = await labelConnectedComponents3D(
        sdfU8,
        nx,
        ny,
        nz,
        threshold,
        false, // labelInside = false (label the background/outside)
        signal
    );

    if (numComponents === 0) {
        // No background - return copy of input
        return sdfU8.map((slice) => new Uint8Array(slice));
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Find which components touch the boundary (those are exterior, not cavities)
    const touchesBoundary = new Uint8Array(numComponents + 1);

    // Check boundary faces only (not all voxels)
    // Z=0 and Z=nz-1 faces
    for (const z of [0, nz - 1]) {
        const zOffset = z * plane;
        for (let i = 0; i < plane; i++) {
            const label = labels[zOffset + i];
            if (label > 0) {
                touchesBoundary[label] = 1;
            }
        }
    }
    // Y=0 and Y=ny-1 edges (excluding corners already covered)
    for (let z = 1; z < nz - 1; z++) {
        const zOffset = z * plane;
        for (const y of [0, ny - 1]) {
            const yOffset = y * nx;
            for (let x = 0; x < nx; x++) {
                const label = labels[zOffset + yOffset + x];
                if (label > 0) {
                    touchesBoundary[label] = 1;
                }
            }
        }
    }
    // X=0 and X=nx-1 edges (excluding all previously covered)
    for (let z = 1; z < nz - 1; z++) {
        const zOffset = z * plane;
        for (let y = 1; y < ny - 1; y++) {
            const yOffset = y * nx;
            for (const x of [0, nx - 1]) {
                const label = labels[zOffset + yOffset + x];
                if (label > 0) {
                    touchesBoundary[label] = 1;
                }
            }
        }
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Create output - fill any background component that doesn't touch boundary
    const outU8: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        outU8[z] = new Uint8Array(plane);
        const zOffset = z * plane;
        for (let i = 0; i < plane; i++) {
            const label = labels[zOffset + i];

            if (label === 0) {
                // Inside the structure - keep original value
                outU8[z][i] = sdfU8[z][i];
            } else if (touchesBoundary[label]) {
                // Exterior background - keep original value
                outU8[z][i] = sdfU8[z][i];
            } else {
                // Cavity (background not connected to boundary) - fill it
                outU8[z][i] = 255;
            }
        }

        // Yield every few slices
        if (z % 20 === 0) {
            lastYieldTick = await maybeYield(lastYieldTick, signal);
        }
    }

    return outU8;
}
